import {OptionHTMLAttributes, useEffect} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import Select from '../../../app/common/components/select/Select';
import {ReclamationReason, ReclamationTypes} from '../Type/reclamation';
import {SetFieldValue} from "../../../app/types/Forms";

interface SelectReasonProps {
  type: string;
  name?: string;
  value?: OptionHTMLAttributes<string>;
  setFieldValue: SetFieldValue;
  isLoading?: boolean;
  isDisabled?: boolean;
  touched?: boolean;
  error?: string;
}

const administrativeReasonOptions = (intl: IntlShape): OptionHTMLAttributes<string>[] => [
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.INF_FACTURE'}),
        value: ReclamationReason.BILL,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.INF_PAIEMENT'}),
        value: ReclamationReason.PAIEMENT,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.DOUBLE_FACTU'}),
        value: ReclamationReason.DOUBLE_FACTU,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.ERROR_FACTU'}),
        value: ReclamationReason.ERROR_FACTU,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.RESIL_FACTU'}),
        value: ReclamationReason.RESIL_FACTU,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.ERREUR_RESIL'}),
        value: ReclamationReason.ERREUR_RESIL,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.CONTEST_ABO'}),
        value: ReclamationReason.CONTEST_ABO,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.DEMANDE_REMBOURSEMENT'}),
        value: ReclamationReason.DEMANDE_REMBOURSEMENT,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.RECLAMATION_PENALITE'}),
        value: ReclamationReason.DEMANDE_PENALITE,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.CHANGE_RIB'}),
        value: ReclamationReason.CHANGE_RIB,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.CHANGE_JURIDIQUE'}),
        value: ReclamationReason.CHANGE_JURIDIQUE,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.FUSION_ENTREPRISE'}),
        value: ReclamationReason.FUSION_ENTREPRISE,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.PASSAGE_PRELEV'}),
        value: ReclamationReason.PASSAGE_PRELEV,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.PASSAGE_SEPA'}),
        value: ReclamationReason.PASSAGE_SEPA,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.DOC_DUPLICATA'}),
        value: ReclamationReason.DOC_DUPLICATA,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.COMMERCIAL'}),
        value: ReclamationReason.COMMERCIAL,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.LATE_DELIVERY'}),
        value: ReclamationReason.LIVRAISON,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.OTHER'}),
        value: ReclamationReason.OTHER,
    },
];

const technicalReasonOptions = (intl: IntlShape): OptionHTMLAttributes<string>[] => [
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.TECHNICAL.DEGRADATION_OF_SERVICE'}),
        value: ReclamationReason.DEGRADATION_OF_SERVICE,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.TECHNICAL.SERVICE_OUTAGE'}),
        value: ReclamationReason.SERVICE_OUTAGE,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.TECHNICAL.REQUEST_FOR_INFORMATION'}),
        value: ReclamationReason.REQUEST_FOR_INFORMATION,
    },
    {
        label: intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON.TECHNICAL.REQUEST_FOR_CONFIGURATION'}),
        value: ReclamationReason.REQUEST_FOR_CONFIGURATION,
    },
];

export const getReasonOptions = (intl: IntlShape, type: string): OptionHTMLAttributes<string>[] =>
    type === ReclamationTypes.ADMINISTRATIVE
        ? administrativeReasonOptions(intl)
        : technicalReasonOptions(intl);

const SelectReason = ({
                        type,
                        name,
                        value,
                        setFieldValue,
                        isLoading,
                        isDisabled,
                        touched,
                        error,
                      }: SelectReasonProps): JSX.Element => {
  const intl = useIntl();
  const reasonOptions = getReasonOptions(intl, type);

    useEffect(() => {
        if (!value) {
            setFieldValue('reason', undefined);
        }
    }, [type, value, setFieldValue]);

  return (
    <Select<OptionHTMLAttributes<string>>
      label={intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.REASON'})}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isClearable={false}
      isSearchable={false}
      name={name}
      onChange={(reason) => {
        setFieldValue('reason', reason);
      }}
      value={value}
      getOptionValue={(option) => option.value?.toString() ?? ''}
      getOptionLabel={(option) => option.label ?? ''}
      options={reasonOptions}
      placeholder={intl.formatMessage({id: 'TICKET.SELECT.REASON_PLACEHOLDER'})}
      touched={touched}
      error={error}
    />
  );
};

export default SelectReason;
